/** The messages module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiDelete, apiGet, apiGetPage, apiPatch, apiPost } from '../core/index.js'
import type { ContactAddress, ContactsView, ContactView, DeliveryReceipt, FacilityDeliveryView, InboundText, RegisterContactInput, RegisteredContact, ReplyToBarrierInput, SendStaffMessageInput, StaffMessage, StaffMessageWithReach, SubmitBarrierInput, UpdateSmsGroupsInput, WorkerBarrier, WorkerNotification } from './types.js'

/**
 * A worker's unexpired notifications. `live` is for the worker's own app:
 * refetched every minute while open and when the tab regains focus, which is
 * the in-app delivery the plan settles on (W4) - no push, no polling cost
 * worth measuring at today's sizes.
 */
export function useWorkerNotifications(workerId: string | null, { live = false }: { live?: boolean } = {}) {
  return useQuery({
    queryKey: ['worker-notifications', workerId],
    queryFn: () => apiGet<WorkerNotification[]>(`/workers/${workerId as string}/notifications`),
    enabled: workerId !== null,
    ...(live ? { refetchInterval: LIVE_NOTIFICATIONS_INTERVAL_MS, refetchOnWindowFocus: true } : {}),
  })
}

const LIVE_NOTIFICATIONS_INTERVAL_MS = 60_000

/** The worker marking their own messages read: the named ones, or all when none are named. The answer replaces the cached list. */
export function useReadNotifications(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationIds?: readonly string[]) =>
      apiPost<WorkerNotification[]>(`/workers/${encodeURIComponent(workerId)}/notifications/read`, notificationIds ? { notificationIds } : {}),
    onSuccess: (notifications) => {
      queryClient.setQueryData(['worker-notifications', workerId], notifications)
    },
  })
}

/** What a worker has said is getting in the way, newest first. The worker reads their own; staff read any worker they may see. */
export function useWorkerBarriers(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-barriers', workerId],
    queryFn: () => apiGet<WorkerBarrier[]>(`/workers/${workerId as string}/barriers`),
    enabled: workerId !== null,
  })
}

/** The worker writes a note about a behavior that is costing them points. Their list and the facility's refetch. */
export function useSubmitBarrier(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SubmitBarrierInput) => apiPost<WorkerBarrier>(`/workers/${workerId}/barriers`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['worker-barriers', workerId] })
      void queryClient.invalidateQueries({ queryKey: ['facility-barriers'] })
    },
  })
}

/** Every note written at a facility, newest first. Staff only. */
export function useFacilityBarriers(facilityId: string | null) {
  return useQuery({
    queryKey: ['facility-barriers', facilityId],
    queryFn: () => apiGet<WorkerBarrier[]>(`/facilities/${facilityId as string}/barriers`),
    enabled: facilityId !== null,
    // Asked again every minute while the facility is on screen, so a note a worker has just written shows on the Messages tab's count without a reload.
    refetchInterval: BARRIERS_REFRESH_MS,
  })
}

const BARRIERS_REFRESH_MS = 60_000

/** How many of a facility's notes from workers nobody has marked seen yet: the count on the Messages tab. Zero while loading or where the notes cannot be read. */
export function useUnseenBarrierCount(facilityId: string | null): number {
  return (useFacilityBarriers(facilityId).data ?? []).filter((barrier) => barrier.acknowledgement === null).length
}

/** An operator or facility administrator marks a worker's note seen. */
export function useAcknowledgeBarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workerId, barrierId }: { workerId: string; barrierId: string }) => apiPost<WorkerBarrier>(`/workers/${workerId}/barriers/${barrierId}/acknowledge`, {}),
    onSuccess: (barrier) => {
      void queryClient.invalidateQueries({ queryKey: ['worker-barriers', barrier.workerId] })
      void queryClient.invalidateQueries({ queryKey: ['facility-barriers', barrier.facilityId] })
    },
  })
}

/**
 * An operator or facility administrator answers a worker's note, once. The
 * API also marks it seen and sends the worker a message, so the worker's
 * notifications refetch along with both barrier lists.
 */
export function useReplyToBarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workerId, barrierId, message }: { workerId: string; barrierId: string; message: string }) =>
      apiPost<WorkerBarrier>(`/workers/${workerId}/barriers/${barrierId}/reply`, { message } satisfies ReplyToBarrierInput),
    onSuccess: (barrier) => {
      void queryClient.invalidateQueries({ queryKey: ['worker-barriers', barrier.workerId] })
      void queryClient.invalidateQueries({ queryKey: ['facility-barriers', barrier.facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['worker-notifications', barrier.workerId] })
    },
    // Someone else answered first (409) or the note is gone (404): refetch so the list stops offering a reply.
    onError: (error, { workerId }) => {
      if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        void queryClient.invalidateQueries({ queryKey: ['worker-barriers', workerId] })
        void queryClient.invalidateQueries({ queryKey: ['facility-barriers'] })
      }
    },
  })
}

/** How a worker can be reached beyond the app, and what this deployment can send on. The worker reads their own; staff read any worker they may see. */
export function useWorkerContacts(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-contacts', workerId],
    queryFn: () => apiGet<ContactsView>(`/workers/${encodeURIComponent(workerId as string)}/contacts`),
    enabled: workerId !== null,
  })
}

function invalidateContacts(queryClient: ReturnType<typeof useQueryClient>, workerId: string): void {
  void queryClient.invalidateQueries({ queryKey: ['worker-contacts', workerId] })
}

/** A worker's replies by text that were not keywords, newest first (SMS plan D5); the worker's own, or staff's read of any worker they may see. */
export function useWorkerInboundTexts(workerId: string | null, limit = 20) {
  return useQuery({
    queryKey: ['worker-inbound-texts', workerId, limit],
    queryFn: () => apiGetPage<InboundText>(`/workers/${encodeURIComponent(workerId as string)}/inbound-texts?limit=${limit}`, null),
    enabled: workerId !== null,
  })
}

/** The messages staff sent at a facility, newest first (SMS plan 3.7). */
export function useFacilityMessages(facilityId: string | null, limit = 30) {
  return useQuery({
    queryKey: ['facility-messages', facilityId, limit],
    queryFn: () => apiGetPage<StaffMessage>(`/facilities/${encodeURIComponent(facilityId as string)}/messages?limit=${limit}`, null),
    enabled: facilityId !== null,
  })
}

/** One message with how it reached its recipients, counted on request. */
export function fetchStaffMessage(facilityId: string, messageId: string): Promise<StaffMessageWithReach> {
  return apiGet<StaffMessageWithReach>(`/facilities/${encodeURIComponent(facilityId)}/messages/${encodeURIComponent(messageId)}`)
}

/** Staff write to a worker or the roster; the sent list and the recipients' messages are read again after. */
export function useSendStaffMessage(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SendStaffMessageInput) => apiPost<StaffMessage>(`/facilities/${encodeURIComponent(facilityId)}/messages`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facility-messages', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['worker-notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['worker-deliveries'] })
    },
  })
}

/** The worker agrees to a number their facility entered (SMS plan 3.5): the consent is recorded in the app's words and a code goes out; verify as usual. */
export function useAgreeContact(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, wording }: { contactId: string; wording: string }) => apiPost<RegisteredContact>(`/workers/${encodeURIComponent(workerId)}/contacts/${encodeURIComponent(contactId)}/agree`, { consent: { wording } }),
    onSettled: () => invalidateContacts(queryClient, workerId),
  })
}

/** The worker's choice of which groups of messages reach them by text (SMS plan section 3.2); the answer is the contacts read with every group stated. */
export function useUpdateSmsGroups(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (smsGroups: UpdateSmsGroupsInput) => apiPatch<ContactsView>(`/workers/${encodeURIComponent(workerId)}/contacts/preferences`, { smsGroups }),
    onSuccess: () => invalidateContacts(queryClient, workerId),
  })
}

/** The facility's rules over texts with every notification type stated by group: what the Notifications tab lays out. */
export function useFacilityDelivery(facilityId: string | null) {
  return useQuery({
    queryKey: ['facility-delivery', facilityId],
    queryFn: () => apiGet<FacilityDeliveryView>(`/facilities/${encodeURIComponent(facilityId as string)}/delivery`),
    enabled: facilityId !== null,
  })
}

/** The worker registers an address on a channel; push is good at once, email and SMS get a code to prove. */
export function useRegisterContact(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RegisterContactInput) => apiPost<RegisteredContact>(`/workers/${encodeURIComponent(workerId)}/contacts`, input),
    onSuccess: () => invalidateContacts(queryClient, workerId),
  })
}

/** The worker proves the six-digit code sent to an address. */
export function useVerifyContact(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, code }: { contactId: string; code: string }) => apiPost<ContactView>(`/workers/${encodeURIComponent(workerId)}/contacts/${encodeURIComponent(contactId)}/verify`, { code }),
    onSettled: () => invalidateContacts(queryClient, workerId),
  })
}

/** The worker's switch for one contact. */
export function useUpdateContact(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, enabled }: { contactId: string; enabled: boolean }) => apiPatch<ContactView>(`/workers/${encodeURIComponent(workerId)}/contacts/${encodeURIComponent(contactId)}`, { enabled }),
    onSuccess: () => invalidateContacts(queryClient, workerId),
  })
}

/** The worker forgets an address. */
export function useForgetContact(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => apiDelete(`/workers/${encodeURIComponent(workerId)}/contacts/${encodeURIComponent(contactId)}`),
    onSuccess: () => invalidateContacts(queryClient, workerId),
  })
}

/** Where a worker's messages went: the newest receipts, or one message's. The worker reads their own; staff read any worker they may see. */
export function useWorkerDeliveries(workerId: string | null, options: { notificationId?: string | null; limit?: number } = {}) {
  const notificationId = options.notificationId ?? null
  const limit = options.limit ?? 50
  return useQuery({
    queryKey: ['worker-deliveries', workerId, notificationId, limit],
    queryFn: () => apiGetPage<DeliveryReceipt>(`/workers/${encodeURIComponent(workerId as string)}/deliveries?limit=${limit}${notificationId ? `&notificationId=${encodeURIComponent(notificationId)}` : ''}`, null),
    enabled: workerId !== null,
  })
}

/** One contact's address whole, for the eye beside a masked number; a plain call, so it never sits in the query cache. */
export function fetchContactAddress(workerId: string, contactId: string): Promise<ContactAddress> {
  return apiGet<ContactAddress>(`/workers/${encodeURIComponent(workerId)}/contacts/${encodeURIComponent(contactId)}/address`)
}
